import React, { useState, useEffect } from 'react';
import { Users as UsersIcon, Plus, User as UserIcon, Mail, Lock, X, Loader2, Phone, Trash2, Key } from 'lucide-react';
import { apiService, UserProfile, UserCreateRequest } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../contexts/ToastContext';

const Users: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { showToast } = useToast();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resettingUser, setResettingUser] = useState<UserProfile | null>(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await apiService.getUsers();
      setUsers(data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    const formData = new FormData(e.currentTarget);
    const password = formData.get('password') as string;
    const password_confirm = formData.get('password_confirm') as string;

    if (!password || !password_confirm) {
      setError('Password is required');
      setSubmitting(false);
      return;
    }

    if (password !== password_confirm) {
      setError('Passwords do not match');
      setSubmitting(false);
      return;
    }

    let phone = formData.get('phone') as string;
    if (phone) {
      // Basic sanitization: remove spaces, dashes, etc. but keep + and digits
      phone = phone.replace(/[^0-9+]/g, '');
    }

    const normalizedPhone = phone || undefined;
    const selectedRole = formData.get('role') as 'super_admin' | 'admin' | 'content_manager';

    const userData: UserCreateRequest = {
      username: ((formData.get('username') as string) || '').trim(),
      email: ((formData.get('email') as string) || '').trim() || undefined,
      first_name: ((formData.get('first_name') as string) || '').trim() || undefined,
      last_name: ((formData.get('last_name') as string) || '').trim() || undefined,
      phone: normalizedPhone,
      role: selectedRole,
      password: password,
      password_confirm: password_confirm,
    };

    try {
      await apiService.createUser(userData);
      showToast('User created successfully!', 'success');
      setShowForm(false);
      setError(null);
      fetchUsers();
    } catch (err: unknown) {
      handleApiError(err, 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!resettingUser) return;

    setSubmitting(true);
    setError(null);

    const formData = new FormData(e.currentTarget);
    const new_password = formData.get('new_password') as string;
    const new_password_confirm = formData.get('new_password_confirm') as string;

    if (new_password !== new_password_confirm) {
      setError('Passwords do not match');
      setSubmitting(false);
      return;
    }

    try {
      await apiService.resetUserPassword({
        user_id: resettingUser.id,
        new_password,
        new_password_confirm
      });
      setShowResetModal(false);
      setResettingUser(null);
      showToast(`Password for ${resettingUser.username} has been reset successfully.`, 'success');
    } catch (err: unknown) {
      handleApiError(err, 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (user: UserProfile) => {
    if (!window.confirm(`Are you sure you want to delete user @${user.username}? This action cannot be undone.`)) {
      return;
    }

    try {
      await apiService.deleteUser(user.id);
      setUsers(users.filter(u => u.id !== user.id));
      showToast(`User @${user.username} deleted successfully.`, 'success');
    } catch (err: unknown) {
      console.error('Failed to delete user:', err);
      showToast('Failed to delete user.', 'error');
    }
  };

  const handleApiError = (err: unknown, defaultMsg: string) => {
    const error = err as any;
    console.error(defaultMsg + ':', error);
    const data = error.response?.data;
    if (data) console.error('Server error response:', data);

    let errorMessage = defaultMsg;

    if (data) {
      if (typeof data === 'string') {
        errorMessage = data;
      } else if (data.detail) {
        errorMessage = data.detail;
      } else if (typeof data === 'object') {
        const errorList = Object.entries(data)
          .map(([field, errors]) => {
            const fieldName = field.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
            if (typeof errors === 'string' && errors.includes('Authentication required')) {
              return `${fieldName}: Backend rejected role assignment (Check backend serializer context)`;
            }
            const errorText = Array.isArray(errors) ? errors.join(', ') : errors;
            return `${fieldName}: ${errorText}`;
          });
        if (errorList.length > 0) {
          errorMessage = errorList.join(' | ');
        }
      }
    }
    setError(errorMessage);
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'super_admin':
        return <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-[10px] font-bold uppercase tracking-wider">Super Admin</span>;
      case 'admin':
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-[10px] font-bold uppercase tracking-wider">Admin</span>;
      case 'content_manager':
        return <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-[10px] font-bold uppercase tracking-wider">Content Manager</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-[10px] font-bold uppercase tracking-wider">{role || 'User'}</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">System Users</h1>
          <p className="text-gray-500 mt-1">Manage staff accounts and access levels (Creation and View only)</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center px-6 py-2.5 bg-orange-600 text-white rounded-xl hover:bg-orange-700 transition-all shadow-lg shadow-orange-100 font-bold text-sm"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create New User
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
            <div className="px-8 py-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Create Staff Account</h3>
                <p className="text-xs text-gray-500 mt-0.5 font-medium">Add a new member to the admin panel</p>
              </div>
              <button
                onClick={() => { setShowForm(false); setError(null); }}
                className="p-2 hover:bg-gray-200/50 rounded-full transition-colors text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-8 space-y-5 overflow-y-auto">
              {error && (
                <div className="p-4 bg-red-50 border border-red-100 text-red-600 text-xs font-bold rounded-xl animate-shake">
                  {error}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider ml-1">Username</label>
                  <div className="relative">
                    <UserIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      name="username"
                      required
                      autoComplete="off"
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:ring-2 focus:ring-orange-500/20 focus:bg-white outline-none transition-all placeholder:text-gray-300"
                      placeholder="johndoe"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider ml-1">Role</label>
                  <select
                    name="role"
                    required
                    defaultValue="content_manager"
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-orange-500/20 focus:bg-white outline-none transition-all font-medium"
                  >
                    <option value="content_manager">Content Manager</option>
                    {currentUser?.role === 'super_admin' && (
                      <>
                        <option value="admin">Administrator</option>
                        <option value="super_admin">Super Administrator</option>
                      </>
                    )}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider ml-1">First Name</label>
                  <input
                    name="first_name"
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-orange-500/20 focus:bg-white outline-none transition-all placeholder:text-gray-300"
                    placeholder="John"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider ml-1">Last Name</label>
                  <input
                    name="last_name"
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-orange-500/20 focus:bg-white outline-none transition-all placeholder:text-gray-300"
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider ml-1">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:ring-2 focus:ring-orange-500/20 focus:bg-white outline-none transition-all placeholder:text-gray-300"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider ml-1">Phone Number</label>
                <div className="relative">
                  <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    name="phone"
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:ring-2 focus:ring-orange-500/20 focus:bg-white outline-none transition-all placeholder:text-gray-300"
                    placeholder="+1234567890"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider ml-1">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="password"
                      name="password"
                      required
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:ring-2 focus:ring-orange-500/20 focus:bg-white outline-none transition-all placeholder:text-gray-300 font-mono"
                      placeholder="••••••••"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider ml-1">Confirm Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="password"
                      name="password_confirm"
                      required
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:ring-2 focus:ring-orange-500/20 focus:bg-white outline-none transition-all placeholder:text-gray-300 font-mono"
                      placeholder="••••••••"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-6 border-t border-gray-50">
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setError(null); }}
                  className="px-6 py-2.5 text-gray-500 font-bold text-sm hover:text-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-8 py-2.5 bg-orange-600 text-white rounded-xl font-bold text-sm hover:bg-orange-700 transition-all flex items-center gap-2 shadow-lg shadow-orange-100 disabled:opacity-50"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Account'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {users.map((user) => (
          <div key={user.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col hover:shadow-xl hover:shadow-gray-100 transition-all duration-300 group ring-1 ring-transparent hover:ring-orange-100">
            <div className="flex items-start justify-between mb-5">
              <div className="h-14 w-14 rounded-2xl bg-gray-50 text-gray-400 flex items-center justify-center border border-gray-100 group-hover:bg-orange-50 group-hover:text-orange-500 group-hover:border-orange-100 transition-all duration-300 overflow-hidden">
                {user.profile_image ? (
                  <img src={user.profile_image} alt={user.username} className="h-full w-full object-cover" />
                ) : (
                  <UserIcon className="h-7 w-7" />
                )}
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className={`px-2 py-1 rounded-full text-[8px] font-black uppercase tracking-tighter ${user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {user.is_active ? 'Active' : 'Inactive'}
                </div>
                {getRoleBadge(user.role)}
                <button
                  onClick={() => { setResettingUser(user); setShowResetModal(true); }}
                  className="mt-2 p-1.5 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-all flex items-center gap-1 text-[10px] font-bold"
                  title="Reset Password"
                >
                  <Key className="h-3 w-3" />
                  Reset Pass
                </button>
                {currentUser?.role === 'super_admin' && (
                  <button
                    onClick={() => handleDeleteUser(user)}
                    className="mt-2 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all flex items-center gap-1 text-[10px] font-bold"
                    title="Delete User"
                  >
                    <Trash2 className="h-3 w-3" />
                    Delete
                  </button>
                )}
              </div>
            </div>

            <div className="mb-5">
              <h3 className="font-bold text-gray-900 text-lg group-hover:text-orange-600 transition-colors">
                {user.first_name ? `${user.first_name} ${user.last_name || ''}` : user.username}
              </h3>
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mt-0.5">@{user.username}</p>
            </div>

            <div className="mt-auto space-y-2.5">
              <div className="flex items-center gap-2.5 text-xs text-gray-500 font-medium bg-gray-50 rounded-lg p-2.5 group-hover:bg-orange-50/30 transition-colors">
                <Mail className="h-3.5 w-3.5 text-gray-400 group-hover:text-orange-400" />
                <span className="truncate">{user.email || 'No email provided'}</span>
              </div>
              {user.phone && (
                <div className="flex items-center gap-2.5 text-xs text-gray-500 font-medium bg-gray-50 rounded-lg p-2.5 group-hover:bg-orange-50/30 transition-colors">
                  <Phone className="h-3.5 w-3.5 text-gray-400 group-hover:text-orange-400" />
                  <span>{user.phone}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {users.length === 0 && (
        <div className="text-center py-24 bg-gray-50 rounded-3xl border border-dashed border-gray-200">
          <div className="h-20 w-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm">
            <UsersIcon className="h-10 w-10 text-gray-200" />
          </div>
          <h3 className="text-xl font-bold text-gray-900">No staff found</h3>
          <p className="text-gray-400 mt-2 text-sm">Start by adding your first system administrator</p>
          <button
            onClick={() => setShowForm(true)}
            className="mt-6 px-6 py-2.5 bg-white border border-gray-200 rounded-xl font-bold text-sm text-gray-600 hover:border-orange-500 hover:text-orange-600 transition-all"
          >
            Create Account
          </button>
        </div>
      )}
      {showResetModal && resettingUser && (
        <div className="fixed inset-0 bg-gray-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden flex flex-col">
            <div className="px-8 py-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Reset Password</h3>
                <p className="text-xs text-gray-500 mt-0.5 font-medium">Resetting password for @{resettingUser.username}</p>
              </div>
              <button
                onClick={() => { setShowResetModal(false); setResettingUser(null); setError(null); }}
                className="p-2 hover:bg-gray-200/50 rounded-full transition-colors text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleResetPassword} className="p-8 space-y-5">
              {error && (
                <div className="p-4 bg-red-50 border border-red-100 text-red-600 text-xs font-bold rounded-xl">
                  {error}
                </div>
              )}

              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider ml-1">New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="password"
                    name="new_password"
                    required
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:ring-2 focus:ring-orange-500/20 focus:bg-white outline-none transition-all font-mono"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider ml-1">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="password"
                    name="new_password_confirm"
                    required
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:ring-2 focus:ring-orange-500/20 focus:bg-white outline-none transition-all font-mono"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-6 border-t border-gray-50">
                <button
                  type="button"
                  onClick={() => { setShowResetModal(false); setResettingUser(null); setError(null); }}
                  className="px-6 py-2.5 text-gray-500 font-bold text-sm hover:text-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-8 py-2.5 bg-gray-900 text-white rounded-xl font-bold text-sm hover:bg-black transition-all flex items-center gap-2 shadow-lg disabled:opacity-50"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm Reset'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;